import { useState } from "react";
import { Search, Phone, MessageCircle, ChevronDown, User, Star, Bot, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "date-fns";

// Filter tabs
const statusLabels = {
  expiring: "⏰ Expiring",
  new: "New",
  unread: "Unread",
  all: "All Chats",
};

export default function ChatList({
  conversations = [],
  selectedConversation,
  onSelectConversation,
  filter,
  onFilterChange,
  searchQuery,
  onSearchChange,
  loading,
  configs = [],
  selectedConfig,
  onConfigChange,
  contacts = [],
  onToggleFavourite,
  onRefresh,
}) {
  // Get contact name by phone number
  const getContactName = (phoneNumber) => {
    const contact = contacts.find(c => c.phone_number === phoneNumber);
    return contact?.name || null;
  };

  const getInitials = (name, phone) => {
    if (name) {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return phone?.slice(-2) || "?";
  };

  const formatTime = (timestamp) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return "";
    }
  };

  // Check if WhatsApp 24h free session is active
  const isSessionActive = (lastIncomingTime) => {
    if (!lastIncomingTime) return false;
    try {
      const lastIncoming = new Date(lastIncomingTime);
      const now = new Date();
      const hoursDiff = (now - lastIncoming) / (1000 * 60 * 60);
      return hoursDiff < 24;
    } catch {
      return false;
    }
  };

  const selectedConfigData = configs.find((c) => c.id === selectedConfig);

  return (
    <aside
      className="w-72 border-r bg-background flex flex-col shrink-0"
      data-testid="chat-list"
    >
      {/* Header */}
      <div className="p-4 border-b space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Conversations</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => onRefresh?.()}
              title="Refresh list"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
            {conversations.filter(c => c.unread_count > 0).length > 0 && (
              <Badge variant="destructive" className="font-mono">
                {conversations.filter(c => c.unread_count > 0).length} unread
              </Badge>
            )}
          </div>
        </div>

        {/* Phone Number Selector */}
        {configs.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between text-sm"
                data-testid="phone-selector"
              >
                <div className="flex items-center gap-2 truncate">
                  <Phone className="h-4 w-4 shrink-0" />
                  <span className="truncate">
                    {selectedConfigData?.phone_display_name || "All Numbers"}
                  </span>
                </div>
                <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-72">
              <DropdownMenuLabel>Select Phone Number</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onConfigChange(null)}>
                All Numbers
              </DropdownMenuItem>
              {configs.map((config) => (
                <DropdownMenuItem
                  key={config.id}
                  onClick={() => onConfigChange(config.id)}
                >
                  <div className="flex flex-col">
                    <span>{config.phone_display_name || "Unnamed"}</span>
                    <span className="text-xs text-muted-foreground font-mono">
                      {config.phone_number_id}
                    </span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, phone, order..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            data-testid="search-conversations"
          />
        </div>

        {/* Filters */}
        <div className="flex gap-1 items-center">
          {Object.entries(statusLabels).map(([key, label]) => (
            <Button
              key={key}
              variant={filter === key ? "default" : "ghost"}
              size="sm"
              className={`flex-1 text-xs ${
                filter === key ? "bg-primary text-primary-foreground" : ""
              }`}
              onClick={() => onFilterChange(key)}
              data-testid={`filter-${key}`}
            >
              {label}
            </Button>
          ))}

        </div>
      </div>

      {/* Conversation List */}
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="p-4 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-2">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-40" />
                </div>
              </div>
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <MessageCircle className="h-12 w-12 text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground">No conversations found</p>
            <p className="text-sm text-muted-foreground/70">
              Messages will appear here when customers contact you
            </p>
          </div>
        ) : (
          <div className="p-2">
            {conversations.map((conversation) => {
              const isSelected = selectedConversation?.id === conversation.id;
              const hasUnread = conversation.unread_count > 0;
              const contactName = getContactName(conversation.phone_number);
              const isFavourite = conversation.is_favourite;
              const sessionActive = isSessionActive(conversation.last_incoming_message_time);
              const timeRemainingHours = conversation.time_remaining_hours;
              const isExpiringSoon = timeRemainingHours !== undefined && timeRemainingHours < 4;
              const isUrgent = timeRemainingHours !== undefined && timeRemainingHours < 1;

              return (
                <div
                  key={conversation.id}
                  className={`
                    w-full text-left p-3 rounded-lg mb-1 transition-all cursor-pointer
                    ${
                      isSelected
                        ? "bg-primary/10 active-indicator"
                        : "hover:bg-muted"
                    }
                  `}
                  onClick={() => onSelectConversation(conversation)}
                  data-testid={`conversation-${conversation.id}`}
                >
                  <div className="flex items-start gap-3 w-full">
                    <div className="relative shrink-0">
                      <Avatar className="h-10 w-10">
                        {conversation.profile_picture ? (
                          <AvatarImage src={conversation.profile_picture} alt="Profile" />
                        ) : null}
                        <AvatarFallback className="bg-slate-100 text-slate-500">
                          <User className="h-5 w-5" />
                        </AvatarFallback>
                      </Avatar>
                      {/* WhatsApp Session Indicator Dot */}
                      <span
                        className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background ${
                          sessionActive ? "bg-green-500" : "bg-red-500"
                        }`}
                        title={sessionActive ? "Free session active" : "Session expired"}
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <span className={`truncate ${hasUnread ? "font-bold text-foreground" : "font-medium text-muted-foreground"}`}>
                          {contactName || conversation.phone_number}
                        </span>
                        {hasUnread && (
                          <span className="bg-primary text-primary-foreground text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center shrink-0">
                            {conversation.unread_count}
                          </span>
                        )}
                        {/* Favourite Star */}
                        <button
                          className="shrink-0 p-0.5 hover:bg-amber-100 rounded"
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleFavourite && onToggleFavourite(conversation.id);
                          }}
                          title={isFavourite ? "Remove from favourites" : "Add to favourites"}
                          data-testid={`star-${conversation.id}`}
                        >
                          <Star 
                            className={`h-4 w-4 ${isFavourite ? "fill-amber-500 text-amber-500" : "text-gray-400 hover:text-amber-500"}`}
                          />
                        </button>
                      </div>
                      
                      {contactName && (
                        <p className="text-xs text-muted-foreground truncate">
                          {conversation.phone_number}
                        </p>
                      )}

                      <p className={`text-sm truncate mt-1 ${hasUnread ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                        {conversation.last_message || "No messages yet"}
                      </p>

                      <div className="flex items-center justify-between mt-1">
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-muted-foreground">
                            {formatTime(conversation.last_message_time)}
                          </p>
                          {/* Expiry indicator */}
                          {isExpiringSoon && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${isUrgent ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              ⏰ {timeRemainingHours < 1 ? `${Math.round(timeRemainingHours * 60)}m` : `${timeRemainingHours.toFixed(1)}h`} left
                            </span>
                          )}
                          {/* AI Handled Badge */}
                          {conversation.ai_handled && (
                            <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium">
                              <Bot className="h-3 w-3" />
                              AI
                            </span>
                          )}
                        </div>
                        {/* Session Badge */}
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                            sessionActive
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                          data-testid={`session-badge-${conversation.id}`}
                        >
                          {sessionActive ? "Active" : "Expired"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </aside>
  );
}
